/** CLP/GHS hazard statement (H, EUH, or P) as carried inside an {@link SdsRecord}. */
export interface SdsHazardStatementRecord {
  Id: number;
  Code: string;
  StatementType: string;
  StatementEn: string;
  StatementNb?: string | null;
  StatementNn?: string | null;
  IsCombined: boolean;
  SortOrder: number;
}

export interface SdsRecordFileVersion {
  Id: number;
  IsCurrent: boolean;
  CreatedOn: Date;
  UploadedOn?: Date | null;
  FileName: string;
  FileUrl: string;
  BlobUrl?: string;
  MimeType?: string;
  Size?: number;
  ShareLinkId?: number | null;
  ShareUrl?: string;
  ShareExpiresOn?: string;
}

export interface SdsRecord {
  Id: number;
  ProductName: string;
  Supplier: string;
  Pictograms: string[];
  /** Comma-joined H-statement codes for the compact list/badge views (e.g. "H315, H319"). Derived from {@link HazardStatements}. */
  HStatementCode: string;
  /** Structured H/EUH/P statements as returned by the backend. Source of truth for the chips/picker. */
  HazardStatements: SdsHazardStatementRecord[];
  RiskFigure: RiskFigureData;
  /** Risk analysis inputs (5×5 matrix). RiskFigure is derived as Probability × Consequence. */
  Probability?: number;
  Consequence?: number;
  SdsFileName: string;
  SdsFileUrl: string;
  CreatedOn: Date;
  EditedBy: string;

  /* ─── Extended fields for the new/edit form ─── */
  CASNumber?: string;
  NoCasNumber?: boolean;
  Description?: string;
  KeyRisk?: string;

  /* Persisted SDS detail fields */
  TaskActivity?: string;
  OtherControls?: string;
  RequiresExposureTracking: boolean;
  HazardCategory?: SdsHazardCategory;
  EmployeeExposures?: SdsEmployeeExposure[] | null;

  /** True when at least one active exposure record exists. Populated by the backend; no PII exposed. */
  HasActiveExposures?: boolean;
  /** True when the chemical is soft-deleted (shown only in the Archived Chemicals view). */
  IsDeleted?: boolean;
  /** Base64-encoded PDF uploaded by the user (data:application/pdf;base64,…) */
  SdsFileData?: string;
  SdsBlobUrl?: string;
  SdsFileUploadedOn?: Date | null;
  QrShareUrl?: string;
  QrShareExpiresOn?: string;
  FileVersions: SdsRecordFileVersion[];
  SelectedFileVersionId?: number | null;
}

export interface SdsEmployeeExposure {
  Id?: number | null;
  SDS_DocumentId?: number | null;
  BusinessId?: number | null;
  EmployeeUserId?: number | null;
  FirstName: string;
  LastName: string;
  Email: string;
  Address: string;
  /** Plaintext NIN, only sent on create/update when the user has reveal/edit rights. */
  NationalIdentityNumber?: string | null;
  /** Masked form for display (e.g. "******12345"). Returned on read. */
  NationalIdentityNumberMasked?: string | null;
  MedicalExamDate?: string | Date | null;
  MedicalExamDoctorName?: string | null;
  ExposureStartDate: string | Date | null;
  ExposureEndDate: string | Date | null;
  RetentionExpiryDate?: string | Date | null;
  LegalHold?: boolean;
  LastChangeReason?: string | null;
}

/**
 * Drives Norwegian exposure-register handling.
 * Must stay in sync with SDS_HazardCategory in the backend.
 */
export enum SdsHazardCategory {
  None = 0,
  Asbestos = 1,
  Carcinogenic = 2,
  Mutagenic = 3,
  Reprotoxic = 4,
  Lead = 5,
  BiologicalAgent = 6,
  IonisingRadiation = 7,
  OtherRegulated = 8
}

export interface RiskFigureData {
  Value: number;
  ValueText: string;
  BackColor: string;
}

/** GHS Pictogram identifiers */
export type GhsPictogram =
  | 'flammable'
  | 'oxidizer'
  | 'compressed-gas'
  | 'corrosive'
  | 'explosive'
  | 'toxic'
  | 'irritant'
  | 'health-hazard'
  | 'environment';

/**
 * One row in the cross-document Exposure Register grid.
 * Mirrors `SDS_ExposureRegisterRowDto` on the backend. The masked NIN is only
 * present for users on the SDS exposure allowlist.
 */
export interface SdsExposureRegisterRow {
  ExposureId: number;
  EmployeeUserId?: number | null;
  FirstName?: string | null;
  LastName?: string | null;
  NationalIdentityNumberMasked?: string | null;
  /** Unmasked person number — only present on rows returned by the audited export endpoint. */
  NationalIdentityNumber?: string | null;
  ExposureStartDate?: string | Date | null;
  ExposureEndDate?: string | Date | null;
  RetentionExpiryDate?: string | Date | null;
  MedicalExamDate?: string | Date | null;
  MedicalExamDoctorName?: string | null;
  LegalHold: boolean;
  CreatedAt?: string | Date | null;
  UpdatedAt?: string | Date | null;
  DocumentId: number;
  ProductName?: string | null;
  Supplier?: string | null;
  CASNumber?: string | null;
  HazardCategory: SdsHazardCategory;
}

/** One row of the exposure-register PII access audit trail. */
export interface SdsExposureAccessLogRow {
  Id: number;
  AccessedOnUtc: string;
  AccessedByUserId: number;
  AccessedByName?: string | null;
  /** Matches SDS_ExposureAccessKind: 1 ListView … 8 RevealFailed. */
  AccessKind: number;
  Reason?: string | null;
  IpAddress?: string | null;
  ExposureId: number;
  EmployeeName?: string | null;
  ProductName?: string | null;
}

/** One row in the substitution-assessment grid. Mirrors SDS_SubstitutionAssessmentRowDto. */
export interface SdsSubstitutionAssessmentRow {
  DocumentId: number;
  Product: string;
  CASNumber?: string | null;
  Supplier?: string | null;
  HazardCategory: number;
  LastAssessmentDate?: string | null;
  ReminderDate?: string | null;
  /** "Active" | "Archived" | "SoftDeleted". */
  Status: string;
  /** Never assessed or last assessed > 12 months ago — drives the red row. */
  IsOverdue: boolean;
  /** Most recent assessment note (shown in the info popup). */
  LastNote?: string | null;
}

/** AI SDS search suggestion */
export interface SdsSearchSuggestion {
  id: number;
  productName: string;
  supplier: string;
  casNumber: string;
  fileName: string;
  fileUrl?: string;
  fileMime?: string;
  matchScore: number;
}

/** Manufacturer name offered in the SDS picker, flagged with its origin. */
export interface SdsSupplierName {
  Name: string;
  /** True when the name comes from the global default manufacturer catalog. */
  IsDefault: boolean;
}

/** Dropdown option for the manufacturer picker, carrying the catalog-default flag for the badge. */
export interface SdsSupplierOption {
  label: string;
  value: string | null;
  isDefault: boolean;
}
